import React from 'react'

import { Formik } from 'formik'

import { AdminContext } from 'contexts/AdminContext'
import { ZariContext } from '_zari/context'

import { Button, Col, Form, Row } from '_zari/core'
import Yup from '_zari/validation/_yup'

import {
  AfectacionesCompraConstant,
  OrigenesContabilidadConstant,
  AfectacionesVentaConstant,
  ModalidadCalculosDeDepreciacionConstant
} from 'constants/index'

import { IdGen } from 'utils'

import * as FormGroups from './formGroups'

export const ValidationSchema = Yup.object().shape({
  _idCentroCostoNivel1: Yup.string().when('flagCentroCosto', {
    is: true,
    then: Yup.string().required()
  }),
  _idCentroCostoNivel2: Yup.string().when('flagCentroCosto', {
    is: true,
    then: Yup.string().required()
  }),
  cuentaContable: Yup.string().required(),
  importe: Yup.number().required(),
  tipoAfectacion: Yup.string().required()
})

const DetallesCabeceraSection = ({ iOrigen, dispatch, innerFormikRef, mainFormikRef }) => {
  const __adminContext = React.useContext(AdminContext)
  const __zariContext = React.useContext(ZariContext)

  const inputImporteRef = React.useRef(null)

  const formInitialValues = React.useMemo(() => {
    const tipoAfectacion =
      OrigenesContabilidadConstant.VENTAS === iOrigen ? AfectacionesVentaConstant.GRAVADO : AfectacionesCompraConstant.GRAVADO_A_GRAVADO

    const noIncluyeIgv = OrigenesContabilidadConstant.VENTAS === iOrigen ? false : true

    return {
      __keyCuentaContable: IdGen.newId(),
      _idCentroCostoNivel1: '',
      _idCentroCostoNivel2: '',
      _idActivoFijo: '',
      tipoMovimientoActivo: '2',
      centroCostoNivel1Info: '',
      centroCostoNivel2Info: '',
      cuentaContable: '',
      cuentaDestino: '',
      cuentaDestinoInfo: '',
      cuentaContableInfo: '',
      importe: '',
      flagCentroCosto: false,
      esCuentaActivoFijo: false,
      tipoAfectacion: tipoAfectacion,
      noIncluyeIgv: noIncluyeIgv
    }
  }, [iOrigen])

  const aplicaDestino = React.useCallback(
    cuentaContable => {
      if (__adminContext.modalidadCalculoDeDepreciacion !== ModalidadCalculosDeDepreciacionConstant.CLASE9) {
        return false
      }

      if (!cuentaContable) {
        return false
      }

      if (
        cuentaContable.startsWith('61') ||
        cuentaContable.startsWith('62') ||
        cuentaContable.startsWith('63') ||
        cuentaContable.startsWith('64') ||
        cuentaContable.startsWith('65') ||
        cuentaContable.startsWith('67')
      ) {
        return true
      } else {
        return false
      }
    },
    [__adminContext.modalidadCalculoDeDepreciacion]
  )

  const [activoFijoAnteriorData, setActivoFijoAnteriorData] = React.useState({})

  const onSubmit = React.useCallback(
    (data, { resetForm, setSubmitting }) => {
      if (!data.noIncluyeIgv) {
        data.importe = data.importe / 1.18
      }

      if (aplicaDestino(data.cuentaContable) && !data.cuentaDestino) {
        __zariContext.snack('error', 'Ingresar cuenta destino.')
        setSubmitting(false)
        return
      }

      if (data.esCuentaActivoFijo) {
        if (!data.tipoMovimientoActivo || !data._idActivoFijo) {
          __zariContext.snack('error', 'Informacion de activo fijo es requerido.')
          setSubmitting(false)
          return
        }
      }

      dispatch({ type: 'agregar', payload: data })

      resetForm({
        values: {
          ...formInitialValues,
          _idCentroCostoNivel1: '',
          _idCentroCostoNivel2: '',
          centroCostoInfo: '',
          cuentaContable: '',
          cuentaContableInfo: '',
          cuentaDestino: '',
          cuentaDestinoInfo: '',
          __keyCuentaContable: IdGen.newId()
        },
        isSubmitting: false
      })

      inputImporteRef.current.focus()
    },
    [formInitialValues, dispatch, aplicaDestino, __zariContext]
  )

  return (
    <>
      <Formik
        innerRef={innerFormikRef}
        initialValues={formInitialValues}
        onSubmit={onSubmit}
        validationSchema={ValidationSchema}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {({ errors, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values }) => (
          <Form>
            <Row>
              <Col md={2}>
                <FormGroups.ImporteFormGroup
                  innerRef={inputImporteRef}
                  iError={errors.importe}
                  iTouched={touched.importe}
                  fIsSubmitting={isSubmitting}
                  noIncluyeIgv={values.noIncluyeIgv}
                  vCuentaContable={values.cuentaContable}
                  vTipoAfectacion={values.tipoAfectacion}
                  vCentroCosto={values._idCentroCostoNivel1}
                  vFlagCentroCosto={values.flagCentroCosto}
                  vCuentaDestino={values.cuentaDestino}
                  aplicaDestino={aplicaDestino(values.cuentaContable)}
                />
              </Col>
              <Col md={3}>
                <FormGroups.CuentaContableFormGroup
                  key={values.__keyCuentaContable}
                  iError={errors.cuentaContable}
                  iTouched={touched.cuentaContable}
                  iValue={values.cuentaContableInfo}
                  setFieldValue={setFieldValue}
                />
              </Col>
              <Col md={1}>
                <FormGroups.TipoAfectacionFormGroup
                  iOrigen={iOrigen}
                  iError={errors.tipoAfectacion}
                  iTouched={touched.tipoAfectacion}
                  fIsSubmitting={isSubmitting}
                  flagCentroCosto={values.flagCentroCosto}
                  esCuentaActivoFijo={values.esCuentaActivoFijo}
                  aplicaDestino={aplicaDestino(values.cuentaContable)}
                />
              </Col>
              {values.esCuentaActivoFijo ? (
                <Col md={4}>
                  <FormGroups.ActivoFijoFormGroup
                    iError={errors._idActivoFijo}
                    iTouched={touched._idActivoFijo}
                    iCuentaContable={values.cuentaContable}
                    fIsSubmitting={isSubmitting}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    fechaDocumento={mainFormikRef.current.values.fechaDocumento}
                    activoFijoAnteriorData={activoFijoAnteriorData}
                    setActivoFijoAnteriorData={setActivoFijoAnteriorData}
                  />
                </Col>
              ) : values.flagCentroCosto ? (
                <>
                  <Col md={1}>
                    <FormGroups.CentroCostoNivel1FormGroup
                      iError={errors._idCentroCostoNivel1}
                      iTouched={touched._idCentroCostoNivel1}
                      fIsSubmitting={isSubmitting}
                      handleChange={handleChange}
                      setFieldValue={setFieldValue}
                    />
                  </Col>
                  {values._idCentroCostoNivel1 && (
                    <Col md={2}>
                      <FormGroups.CentroCostoNivel2FormGroup
                        iError={errors._idCentroCostoNivel2}
                        iTouched={touched._idCentroCostoNivel2}
                        setFieldValue={setFieldValue}
                        iValue={values.centroCostoNivel2Info}
                        idNodoPadre={values._idCentroCostoNivel1}
                        vOrigen={iOrigen}
                        aplicaDestino={aplicaDestino(values.cuentaContable)}
                      />
                    </Col>
                  )}
                </>
              ) : null}
              {iOrigen === OrigenesContabilidadConstant.COMPRAS && aplicaDestino(values.cuentaContable) ? (
                <Col md={2}>
                  <FormGroups.CuentaDestinoFormGroup
                    iError={errors.cuentaDestino}
                    iTouched={touched.cuentaDestino}
                    fIsSubmitting={isSubmitting}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                  />
                </Col>
              ) : null}
              <Col md={2}>
                <Form.Group>
                  <Form.Label text='&ensp;' md={12} />
                  <Col md={12}>
                    <Button id='nuevoasiento-agregar-button' block onClick={handleSubmit}>
                      Agregar
                    </Button>
                  </Col>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default React.memo(DetallesCabeceraSection)
